






























































































































































































































































































































































































































































import { Card, ConfirmButton, Scrollbar, TwButton } from '@/app/components';
import { S } from '@/app/utilities';
import {
    alphanumericValidator,
    maxLengthValidator,
    minLengthValidator,
    regexValidator,
    requiredValidator,
} from '@/app/validators';
import VueTagsInput from '@johmun/vue-tags-input';
import { ExclamationCircleIcon, PencilAltIcon, TrashIcon } from '@vue-hero-icons/outline';
import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { ModelsAPI } from '../api';
import { useDataModel } from '../composable';
import { ConceptCreationOptions, Status } from '../constants';
import ImportConceptFromModel from './ImportConceptFromModel.vue';
import Metadata from './Metadata.vue';
import StandardsMapping from './StandardsMapping.vue';

extend('required', requiredValidator);
extend('min', minLengthValidator);
extend('max', maxLengthValidator);
extend('alphanumeric', alphanumericValidator);
// giving a custom validator name in order to not be overriden by other 'regex' default validators
extend('concept_title_regex_validator', {
    ...regexValidator,
    message: 'Title must begin with a letter.',
});

export default defineComponent({
    name: 'CreateConcept',
    props: {
        model: {
            type: Object,
            required: true,
        },

        collapsedLeftPane: {
            type: Boolean,
            required: true,
        },
        parent: {
            type: Object,
            required: true,
        },
        highLevelConcepts: {
            type: Array,
            required: false,
        },

        stableModels: {
            type: Array,
            required: true,
        },
        openStandardMappingFormToggle: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        ValidationObserver,
        ValidationProvider,
        TwButton,
        VueTagsInput,
        Card,
        ConfirmButton,
        Scrollbar,
        StandardsMapping,
        Metadata,
        ImportConceptFromModel,
        PencilAltIcon,
        TrashIcon,
        ExclamationCircleIcon,
    },
    setup(props, { root, emit }) {
        // UI variables
        const highLevelConcept = computed(() => props.parent && !props.parent.parentId);
        const loadingRef = ref(false);
        const requiredFieldText = 'This field is required';
        const newConceptRef = ref<any>(null);
        const hovered = ref(false);
        const editPrefixDescription = ref<any>(null);
        const relatedTermError = ref<string | null>(null);
        const cancelPrefix = ref({ prefix: null, description: null });
        const newRelatedConcept = ref({ prefix: null, description: null });
        const selectedDataModelHLConcepts = ref<[]>([]);
        const chooseDataModel = ref<string>('new_concept');
        const addNewRelatedConcept = ref(false);
        const tag = ref('');
        const standardsMapping = ref<any>();

        const loading = computed({
            get: () => loadingRef.value,
            set: (newLoading: boolean) => {
                loadingRef.value = newLoading;
                emit('loading', newLoading);
            },
        });
        const copiedModel = computed(() => props.model);
        const { isDraft, isUnderRevision } = useDataModel(copiedModel);

        const filteredActiveHLConcepts = computed<any>(() => {
            if (!props.highLevelConcepts) return [];

            if (isDraft.value) {
                return props.highLevelConcepts.filter(
                    (h: any) => h.status === Status.Draft && props.parent.id !== h.id,
                );
            }

            if (isUnderRevision.value) {
                return props.highLevelConcepts.filter(
                    (h: any) =>
                        (h.status === Status.UnderRevision || h.status === Status.Draft) && props.parent.id !== h.id,
                );
            }

            return props.highLevelConcepts.filter((h: any) => h.status === Status.Stable && props.parent.id !== h.id);
        });

        const activeHLConcepts = computed(() => {
            return selectedDataModelHLConcepts.value;
        });
        const newConcept = ref<any>({});
        const selectedConceptToBeUsed = ref<{ id: number; action: string } | null>(null);

        const changeConcept = (hlConcept: boolean) => {
            newConcept.value = {
                name: null,
                description: null,
                relatedTerms: [],
                standardsMapping: null,
                referenceConceptName: null,
                referenceConceptId: null,
                referencePrefix: [],
                parentId: props.parent.id,
                status: props.parent.status,
                metadata: null,
                tokenizedTerms: null,
                type: hlConcept ? 'object' : null,
                version: props.parent.version,
                domainUid: hlConcept ? props.parent.uid : props.parent.domainUid,
            };
            if (newConceptRef.value !== null) {
                newConceptRef.value.reset();
            }
            tag.value = '';
            selectedConceptToBeUsed.value = null;
        };

        watch(
            () => highLevelConcept.value,
            (hlConcept) => changeConcept(hlConcept),
        );
        changeConcept(highLevelConcept.value);

        const types = computed(() => {
            if (!highLevelConcept.value) {
                return [
                    { name: 'object', value: 'object' },
                    { name: 'boolean', value: 'boolean' },
                    { name: 'date', value: 'date' },
                    { name: 'datetime', value: 'datetime' },
                    { name: 'double', value: 'double' },
                    { name: 'integer', value: 'integer' },
                    { name: 'string', value: 'string' },
                    { name: 'time', value: 'time' },
                    { name: 'binary', value: 'base64binary' },
                ].sort((a: any, b: any) => a.name.localeCompare(b.name));
            }
            return [{ name: 'object', value: 'object' }];
        });

        const changeTags = (newTags: any) => {
            relatedTermError.value = '';
            const tmpTags = newTags.map((t: any) => t.text);
            newConcept.value.relatedTerms = tmpTags;
        };

        const tags = computed(() => {
            const tmpArray = [] as any;
            newConcept.value.relatedTerms.forEach((r: any) => tmpArray.push({ text: r }));

            return tmpArray;
        });

        const createConcept = async () => {
            if (await newConceptRef.value.validate()) {
                if (!(newConcept.value.name.trim()[0] === '@')) {
                    loading.value = true;
                    newConcept.value.name = newConcept.value.name.trim();

                    if (selectedConceptToBeUsed.value?.id)
                        newConcept.value.externalConceptToBeUsedId = selectedConceptToBeUsed.value.id;
                    const action = selectedConceptToBeUsed.value?.action;
                    if (
                        action === ConceptCreationOptions.FullyImport ||
                        action === ConceptCreationOptions.PartialImport
                    ) {
                        if (props.parent.status === Status.Stable) emit('display-loading-modal', action);
                        else
                            emit(
                                'display-loading-modal',
                                action === ConceptCreationOptions.PartialImport ? 'partial-copy' : 'copy',
                            );

                        ModelsAPI.copyConcept(newConcept.value, action === ConceptCreationOptions.PartialImport)
                            .then((res: any) => {
                                (root as any).$toastr.s('Concept is created successfully', 'Success');

                                if (!res.data.parentId) {
                                    // cloned in review model returned
                                    if (res.data && newConcept.value.parentId !== res.data.id) {
                                        root.$router.push({
                                            name: 'model-manager:edit',
                                            params: { id: res.data.id },
                                        });
                                    }
                                } else {
                                    // copied high level concept returned
                                    emit('created-high-level-concept', res.data);
                                }
                                emit('display-loading-modal', '');
                            })
                            .catch((e) => {
                                emit('display-loading-modal', '');
                                const message = e?.response?.data?.message ? `: ${e.response.data.message}` : '';
                                (root as any).$toastr.e(`Concept failed to be created${message}`, 'Error');
                            })
                            .finally(() => {
                                loading.value = false;
                            });
                    } else {
                        ModelsAPI.createConcept({
                            ...newConcept.value,
                            referenceConceptId:
                                newConcept.value.referenceConceptId || selectedConceptToBeUsed.value?.id,
                        })
                            .then((res: any) => {
                                (root as any).$toastr.s(
                                    `${highLevelConcept.value ? 'Concept' : 'Field'} '${S.sanitizeHtml(
                                        newConcept.value.name,
                                    )}' is created successfully`,
                                    'Success',
                                );
                                if (highLevelConcept.value) {
                                    emit('created-high-level-concept', res.data);
                                } else {
                                    emit('created-field', res.data);
                                }
                            })
                            .catch((e) => {
                                const message = e?.response?.data?.message ? `: ${e.response.data.message}` : '';
                                (root as any).$toastr.e(
                                    `${highLevelConcept.value ? 'Concept' : 'Field'} failed to be created${message}`,
                                    'Error',
                                );
                            })
                            .then(() => {
                                loading.value = false;
                            });
                    }
                } else {
                    (root as any).$toastr.w(
                        `Concept failed to be created: The names of High Level Concepts cannot start with the special character '@'. Please rename the concept.`,
                        'Invalid name',
                    );
                }
            }
        };

        const addRelatedConcept = (relatedConcepts: any) => {
            relatedConcepts.push(newRelatedConcept.value);
            newRelatedConcept.value = { prefix: null, description: null };
            addNewRelatedConcept.value = false;
        };

        const removeRelatedConcept = (index: number) => {
            newConcept.value.referencePrefix.splice(index, 1);
        };

        const updateMetadata = (metadata: any) => {
            newConcept.value.metadata = metadata;
        };

        const validateReferencePrefix = computed(() => {
            return newConcept.value.type === 'object' ? !!newConcept.value.referencePrefix.length : true;
        });

        const validateRelatedTerm = () => {
            if (tag.value.trim()) {
                if (tag.value.length === 50) {
                    relatedTermError.value = 'Maximum number of characters allowed for Related term reached';
                    return false; // returning false here because the tag addition is not allowed by the prop "maxlength", but we want to display this message at exact that number of character
                }
                if (!relatedTermError.value || tag.value.length < 50) {
                    const lowerCaseRelatedTerms = newConcept.value.relatedTerms.map((rt: string) => rt.toLowerCase());
                    if (lowerCaseRelatedTerms.includes(tag.value.toLowerCase()))
                        relatedTermError.value = `Related term '${tag.value}' already exists`;
                    else relatedTermError.value = '';
                }

                return !!relatedTermError.value;
            }
            return false;
        };

        const resetReferenceConcept = () => {
            newConcept.value.metadata = null;

            if (newConcept.value.type !== 'object') {
                newConcept.value.referenceConceptName = null;
                newConcept.value.referencePrefix = [];
                newConcept.value.referenceConceptId = null;
                addNewRelatedConcept.value = false;
            } else {
                newConcept.value.referencePrefix = [];
            }
        };

        const openAddNewRelatedConcept = () => {
            const referenceConcept = filteredActiveHLConcepts.value.find(
                (h: any) => h.id === newConcept.value.referenceConceptId,
            );
            newConcept.value.referenceConceptName = referenceConcept.name;
            addNewRelatedConcept.value = true;
            newRelatedConcept.value = { prefix: null, description: null };
        };

        const openEditRelatedConcept = (index: number, referencePrefix: any) => {
            editPrefixDescription.value = index;
            cancelPrefix.value.prefix = referencePrefix.prefix;
            cancelPrefix.value.description = referencePrefix.description;
        };

        const enableAddNewRelatedConcept = computed(() => {
            return (
                addNewRelatedConcept.value ||
                (newConcept.value.referenceConceptId && !newConcept.value.referencePrefix.length)
            );
        });

        const cancelAddNewRelatedConcept = () => {
            addNewRelatedConcept.value = false;
            newRelatedConcept.value.prefix = null;
            newRelatedConcept.value.description = null;
        };

        const disableCreateConcept = computed(() => {
            return loading.value || (!validateReferencePrefix.value && !highLevelConcept.value);
        });

        const placeholderText = computed(() => {
            return {
                name: `Enter ${highLevelConcept.value ? 'concept' : 'field'} title`,
                description: `Enter a description for the ${highLevelConcept.value ? 'concept' : 'field'}`,
            };
        });

        const resetDataModelChoices = () => {
            if (chooseDataModel.value === ConceptCreationOptions.New) {
                selectedConceptToBeUsed.value = null;

                // reset these if any related terms were added before choosing the import choice and then choosing the new concept choice again
                newConcept.value.relatedTerms = [];
                tag.value = '';
            }
        };

        const disableAddRelatedConceptBtn = computed(
            () =>
                loading.value ||
                !newRelatedConcept.value.prefix ||
                !(newRelatedConcept.value.prefix as string).match(/^[a-z0-9]+$/i) ||
                !newRelatedConcept.value.description,
        );

        const setConceptToBeUsed = (event: { action: string; id: number }) => {
            selectedConceptToBeUsed.value = event;
        };

        const closeStandardsMappingForm = () => {
            if (standardsMapping.value) standardsMapping.value.closeStandardMappingForm();
        };

        return {
            addNewRelatedConcept,
            addRelatedConcept,
            cancelAddNewRelatedConcept,
            cancelPrefix,
            changeTags,
            createConcept,
            disableCreateConcept,
            editPrefixDescription,
            enableAddNewRelatedConcept,
            filteredActiveHLConcepts,
            hovered,
            loading,
            newConcept,
            newConceptRef,
            newRelatedConcept,
            openAddNewRelatedConcept,
            openEditRelatedConcept,
            placeholderText,
            relatedTermError,
            removeRelatedConcept,
            resetReferenceConcept,
            tag,
            tags,
            types,
            updateMetadata,
            validateReferencePrefix,
            activeHLConcepts,
            selectedConceptToBeUsed,
            chooseDataModel,
            resetDataModelChoices,
            disableAddRelatedConceptBtn,
            requiredFieldText,
            setConceptToBeUsed,
            highLevelConcept,
            validateRelatedTerm,
            standardsMapping,
            closeStandardsMappingForm,
        };
    },
});
